import { JournalTemplate } from '../types/journal';

/** The registers a journal line's account can be in: the chart of accounts, customers, vendors or bank accounts. */
export type AccountType = 'gl' | 'customer' | 'vendor' | 'bank';

/** Keys the lists of customers, vendors and bank accounts, followed by the masterdata entity each is read from. */
export const REGISTER_LIST_PREFIX = 'register:';

/** The list an account of each type is picked from. */
const LIST_OF_TYPE: Readonly<Record<AccountType, string>> = {
  gl: 'accounts',
  customer: `${REGISTER_LIST_PREFIX}customers`,
  vendor: `${REGISTER_LIST_PREFIX}vendors`,
  bank: `${REGISTER_LIST_PREFIX}bankAccounts`,
};

/**
 * Words a type cell may hold for each type, lower-cased, as the backend reads them (`SPELLINGS` in its
 * journalAccountTypes.ts): the dropdown's labels, and the Norwegian names Business Central shows, so a
 * type copied from a BC journal reads the same as one picked from the dropdown.
 */
const SPELLINGS: ReadonlyArray<[string, AccountType]> = [
  ['gl', 'gl'], ['g/l account', 'gl'], ['g/l', 'gl'], ['finanskonto', 'gl'],
  ['customer', 'customer'], ['kunde', 'customer'],
  ['vendor', 'vendor'], ['leverandør', 'vendor'],
  ['bank', 'bank'], ['bank account', 'bank'], ['bankkonto', 'bank'],
];
const BY_SPELLING = new Map(SPELLINGS);

/** The type a cell names: undefined when it is blank, null when it names none. */
export function parseAccountType(value: unknown): AccountType | null | undefined {
  const text = value === null || value === undefined ? '' : String(value).trim();
  if (text === '') return undefined;
  return BY_SPELLING.get(text.toLowerCase()) ?? null;
}

/** An account column and what says which register each row's account is in. */
export interface AccountPair {
  /** The account column's index. */
  account: number;
  /** The index of the type column the backend reads for it; null where the template has none. */
  type: number | null;
  /** What a blank or missing type stands for: the template's default, else G/L, as the backend reads it. */
  fallback: AccountType;
}

/**
 * Every account column of a template with its type column, paired as the backend pairs them: the
 * balancing account with the balancing account type, any other account column with the account type.
 */
export function accountPairsOf(template: JournalTemplate): AccountPair[] {
  const columns = template.columnDefinitions;
  const pairs: AccountPair[] = [];
  columns.forEach((col, account) => {
    if (col.masterdataEntity !== 'accounts') return;
    const field = col.linkedField === 'balancingAccount' ? 'balancingAccountType' : 'accountType';
    const type = columns.findIndex(c => c.linkedField === field);
    pairs.push({ account, type: type < 0 ? null : type, fallback: parseAccountType(template.defaultValues?.[field]) ?? 'gl' });
  });
  return pairs;
}

/** The list a row's account is picked from, by the row's type cell; null where the cell names no type. */
export function accountListOf(pair: AccountPair, typeCell: unknown): string | null {
  const named = pair.type === null ? undefined : parseAccountType(typeCell);
  return named === null ? null : LIST_OF_TYPE[named ?? pair.fallback];
}

/** The types a type list offers, from its codes. */
export function typesOffered(codes: unknown[]): AccountType[] {
  const types: AccountType[] = [];
  for (const code of codes) {
    const type = parseAccountType(code);
    if (type && types.indexOf(type) < 0) types.push(type);
  }
  return types;
}

/**
 * The customers, vendors and bank accounts a template's account columns may name: the register of each
 * type its type list offers, and of each default. The chart of accounts is the account column's own list.
 */
export function registerListsOf(template: JournalTemplate, offered: AccountType[]): string[] {
  const keys: string[] = [];
  for (const pair of accountPairsOf(template)) {
    const types = pair.type === null ? [pair.fallback] : offered.concat(pair.fallback);
    for (const type of types) {
      const key = LIST_OF_TYPE[type];
      if (type !== 'gl' && keys.indexOf(key) < 0) keys.push(key);
    }
  }
  return keys;
}
